import { type ReactFlagEvaluationOptions } from '@openfeature/react-sdk';
import { useFlag as useOpenFeatureFlag } from '../../../openfeature/client';
import type { FlagKey, FlagValue } from '../types';
import { useConfidenceOverrides } from '../development-tools';
import {
  coerceFlagValue,
  isSupportedFlagType,
} from '../development-tools/utils';

export function useFlag<K extends FlagKey>(
  key: K,
  defaultFlagValue: FlagValue<K>,
  options?: ReactFlagEvaluationOptions,
) {
  const result = useOpenFeatureFlag<K, FlagValue<K>>(
    key,
    defaultFlagValue,
    options,
  );

  const overrideContext = useConfidenceOverrides();

  if (key in overrideContext.values) {
    const value = overrideContext.values[key];
    const type = result.type;

    if (!isSupportedFlagType(type)) {
      throw new Error(`Unsupported flag type: ${type}`);
    }

    return {
      ...result,
      value: coerceFlagValue(value, type) as FlagValue<K>,
    };
  }

  return {
    value: result.value,
  };
}

export function useSuspenseFlag<K extends FlagKey>(
  key: K,
  defaultFlagValue: FlagValue<K>,
  options?: Omit<
    ReactFlagEvaluationOptions,
    'suspend' | 'suspendUntilReady' | 'suspendWhileReconciling'
  >,
) {
  const result = useFlag<K>(key, defaultFlagValue, {
    ...options,
    suspend: true,
  });

  return {
    value: result.value,
  };
}
